import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useStore from '../store/useStore';
import { reportsAPI, savedReportsAPI } from '../services/api';
import ReportDisplay from './widgets/ReportDisplay';
import ReportEditor from './report-editor/ReportEditor';
import FreshnessBadge from './widgets/FreshnessBadge';
import QueryProgress from './widgets/QueryProgress';
import PeriodSelect from './common/PeriodSelect';
import { periodLabel } from '../utils/cubeQuery';
import { downloadCsv, downloadXlsx } from '../utils/reportExport';
import { exportRows } from '../utils/reportTable';
import useReportResult from '../hooks/useReportResult';
import '../styles/Reports.css';

/**
 * A report on its own page: full-size render, date range and export. A saved
 * report can be edited and deleted. A ready-made report comes from a data
 * source (/reports/ready/:datasetName/:reportKey), runs without being saved, and
 * can be saved as a copy.
 */
const ReportPage = () => {
  const { id, datasetName, reportKey } = useParams();
  const isReady = Boolean(reportKey);
  const navigate = useNavigate();
  const currentCompany = useStore((state) => state.currentCompany);
  const user = useStore((state) => state.user);
  const companyId = currentCompany?.id;
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  const [report, setReport] = useState(null);
  const [dateRange, setDateRange] = useState('');
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);

  const describeError = (err, fallback) => err?.response?.data?.error || err?.message || fallback;

  const load = useCallback(async () => {
    if (!companyId) return;
    try {
      setError('');
      if (isReady) {
        const library = await reportsAPI.library(companyId);
        const found = (library?.data?.reports || []).find((r) => r.datasetName === datasetName && r.key === reportKey);
        if (!found) throw new Error('This ready-made report is not available for this company');
        setReport({
          name: found.title,
          datasetName: found.datasetName,
          cubeQuery: found.cubeQuery,
          display: { displayType: found.displayType },
          refreshMinutes: found.refreshMinutes,
        });
        return;
      }
      const res = await savedReportsAPI.get(id, companyId);
      if (!res?.success) throw new Error(res?.error || 'Report not found');
      setReport(res.data);
    } catch (err) {
      setError(describeError(err, 'Failed to load report'));
    }
  }, [companyId, id, isReady, datasetName, reportKey]);

  // A ready-made report is not saved, so it runs as an unsaved one with the chosen period.
  const runnable = useMemo(() => (report && isReady ? { cubeQuery: report.cubeQuery, display: report.display } : report), [report, isReady]);
  const result = useReportResult({ companyId, report: runnable, dateRange: dateRange || null, limit: 1000, enabled: Boolean(runnable) });
  const table = result.table;
  const hasRows = Boolean(table?.rows?.length);

  useEffect(() => { load(); }, [load]);

  const canEdit = report && !isReady && (isAdmin || report.ownerId === user?.id);

  const saveCopy = async () => {
    try {
      setSaving(true);
      setError('');
      const res = await savedReportsAPI.create(companyId, {
        name: report.name,
        cubeQuery: report.cubeQuery,
        display: report.display,
        refreshMinutes: report.refreshMinutes,
        visibility: isAdmin ? 'company' : 'private',
      });
      if (!res?.success) throw new Error(res?.error || 'Failed to save report');
      navigate(`/reports/${res.data.id}`);
    } catch (err) {
      setError(describeError(err, 'Failed to save report'));
      setSaving(false);
    }
  };

  const handleUpdate = async (_widget, patch) => {
    const res = await savedReportsAPI.update(report.id, companyId, patch);
    if (!res?.success) throw new Error(res?.error || 'Failed to update report');
    setEditing(false);
    setReport(res.data);
    setMessage('Report updated');
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete report "${report.name}"? Widgets showing it will be removed from dashboards.`)) return;
    try {
      await savedReportsAPI.delete(report.id, companyId);
      navigate('/reports');
    } catch (err) {
      setError(describeError(err, 'Failed to delete report'));
    }
  };

  if (error && !report) {
    return <div className="report-page"><div className="error-message">{error}</div><button className="btn-secondary" onClick={() => navigate('/reports')}>All reports</button></div>;
  }
  if (!report) return <div className="report-page"><div className="loading-spinner" /></div>;

  const display = report.display || {};
  const ownTimeDimension = (report.cubeQuery.timeDimensions || [])[0] || null;

  return (
    <div className="report-page">
      <div className="page-header">
        <div>
          <h2>{report.name}</h2>
          <div className="kpi-meta">
            <span>{display.displayType || 'table'}</span>
            {isReady ? <span>Ready-made report</span> : <span>{report.visibility === 'private' ? 'Only me' : 'Company'}</span>}
            {report.ownerUsername && <span>by {report.ownerUsername}</span>}
            <span>refresh every {report.refreshMinutes} min</span>
            {report.datasetName && <code>{report.datasetName}</code>}
          </div>
        </div>
        <div className="page-actions">
          <button className="btn-secondary" onClick={() => navigate('/reports')}>All reports</button>
          {isReady && <button className="btn-primary" onClick={saveCopy} disabled={saving}>{saving ? 'Saving…' : 'Save a copy'}</button>}
          {canEdit && <button className="btn-secondary" onClick={() => setEditing(true)}>Edit</button>}
          {canEdit && <button className="btn-danger" onClick={handleDelete}>Delete</button>}
        </div>
      </div>

      {error && <div className="error-message" style={{ marginBottom: '1rem' }}>{error}</div>}
      {message && <div className="success-message" style={{ marginBottom: '1rem' }}>{message}<button className="error-dismiss" onClick={() => setMessage('')} aria-label="Dismiss">×</button></div>}

      <div className="report-toolbar">
        <div className="report-toolbar-period">
          <span>Period</span>
          <PeriodSelect ariaLabel="Report date range" value={dateRange} onChange={setDateRange} disabled={!ownTimeDimension}
            title={ownTimeDimension ? undefined : 'This report has no date field, so it always covers all data.'}
            emptyLabel={ownTimeDimension ? `Default: ${periodLabel(ownTimeDimension.dateRange)}` : 'All time'} />
        </div>
        <span className="report-toolbar-spacer" />
        <button className="btn-secondary btn-small" disabled={!hasRows} onClick={() => downloadCsv(exportRows(table), report.name)}>Download CSV</button>
        <button className="btn-secondary btn-small" disabled={!hasRows} onClick={() => downloadXlsx(exportRows(table), report.name)}>Download Excel</button>
        <FreshnessBadge kind="report" at={result.at} staleAfterMs={36 * 60 * 60 * 1000} loading={result.loading} />
      </div>

      <div className="report-page-render widget report-widget">
        <div className="widget-body report-body">
          {result.loading && table && <QueryProgress variant="bar" startedAt={result.startedAt} />}
          {result.loading && !table ? <QueryProgress startedAt={result.startedAt} retrying={result.retrying} />
            : result.error ? <div className="report-status report-status--error">⚠ {result.error}</div>
              : table && <ReportDisplay table={table} config={display} />}
        </div>
      </div>

      {editing && (
        <ReportEditor
          onClose={() => setEditing(false)}
          onCreate={() => {}}
          onUpdate={handleUpdate}
          editingWidget={{ id: null, report }}
        />
      )}
    </div>
  );
};

export default ReportPage;
